import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ShieldCheck, Lock, Plus, Pencil, Trash2, Clock, Check, X } from "lucide-react";
import { useAuth } from "../../lib/auth";

// Mirrors PERMISSIONS_BY_MODULE in server.py — keep in sync if the backend
// catalog changes. Reserved permissions never appear here for non-admin.
const PERMISSION_MODULES = [
  { key: "orders", label: "Orders", items: [
    { key: "orders.view", label: "View" },
    { key: "orders.fulfil", label: "Fulfil / dispatch" },
    { key: "orders.edit_cancel_refund", label: "Edit / cancel / refund" },
  ]},
  { key: "inventory", label: "Inventory", items: [
    { key: "inventory.view", label: "View stock" },
    { key: "inventory.adjust_stock", label: "Scan / adjust stock" },
  ]},
  { key: "products", label: "Products & pricing", items: [
    { key: "products.view", label: "View listings" },
    { key: "products.manage", label: "Create / edit / delete listings" },
  ]},
  { key: "financials", label: "Financials", items: [
    { key: "financials.view", label: "Sales & reports" },
    { key: "financials.export", label: "Export reports" },
  ]},
  { key: "discounts", label: "Discounts & promotions", items: [
    { key: "discounts.view", label: "View discounts" },
    { key: "discounts.manage", label: "Create / manage discounts" },
  ]},
  { key: "content", label: "Content", items: [
    { key: "content.manage", label: "Edit blog, banners, FAQ" },
  ]},
  { key: "customers", label: "Customers", items: [
    { key: "customers.view", label: "View customer accounts" },
  ]},
  { key: "returns", label: "Returns", items: [
    { key: "returns.receive_restock", label: "Receive & restock" },
    { key: "returns.approve_refund", label: "Approve refund / exchange" },
  ]},
  { key: "staff", label: "Staff", items: [
     { key: "staff.view", label: "View staff & activity" },
     { key: "staff.create", label: "Add staff member (needs Admin approval)" },
  ]},
  { key: "roles", label: "Role management", items: [
    { key: "roles.create", label: "Draft new roles (needs Admin approval)" },
  ]},
];

const RESERVED_PERMISSIONS = [
  { key: "financials.view_net_margin", label: "Net margin", module: "financials" },
  { key: "admin.manage_roles", label: "Edit/delete roles, approve/reject", module: "roles" },
  { key: "staff.manage", label: "Manage staff accounts", module: "roles" },
];

const STATUS_META = {
  active: { label: "Active", cls: "bg-emerald-50 text-emerald-700" },
  pending: { label: "Pending approval", cls: "bg-amber-50 text-amber-700" },
  rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-700" },
};

const EMPTY_FORM = { name: "", description: "", permissions: [] };

export default function AdminRolesPage() {
  const { user, can } = useAuth();
  const isAdmin = can("admin.manage_roles");
  const canCreate = isAdmin || can("roles.create");

  const [roles, setRoles] = useState([]);
  const [pendingQueue, setPendingQueue] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const calls = [api.get("/admin/roles")];
      if (isAdmin) calls.push(api.get("/admin/roles/pending"));
      const results = await Promise.all(calls);
      setRoles(results[0].data);
      if (isAdmin) setPendingQueue(results[1].data);
    } catch (e) {
      toast.error("Couldn't load roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [isAdmin]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (role) => {
    setEditing(role);
    setForm({ name: role.name, description: role.description || "", permissions: role.permissions || [] });
    setOpen(true);
  };

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const togglePermission = (key) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key) ? f.permissions.filter((p) => p !== key) : [...f.permissions, key],
    }));
  };

  const isEditingOwnRole = editing && user?.role === editing.id;
  const formDisabled = isEditingOwnRole;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Enter a role name first");
      return;
    }
    setSaving(true);
    const payload = { name: form.name.trim(), description: form.description, permissions: form.permissions };
    try {
      if (editing) {
        await api.patch(`/admin/roles/${editing.id}`, payload);
        toast.success("Role updated");
      } else {
        const { data } = await api.post("/admin/roles", payload);
        toast.success(data.status === "pending" ? "Submitted for Admin approval" : "Role created");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (role) => {
    const warning = role.staff_count > 0
      ? `${role.staff_count} staff ${role.staff_count === 1 ? "account" : "accounts"} will lose this role and become unassigned. Delete "${role.name}"?`
      : `Delete "${role.name}"?`;
    if (!window.confirm(warning)) return;
    try {
      await api.delete(`/admin/roles/${role.id}`);
      toast.success("Role deleted");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const approve = async (role) => {
    try {
      await api.post(`/admin/roles/${role.id}/approve`);
      toast.success(`"${role.name}" approved and is now live`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't approve");
    }
  };

  const openReject = (role) => {
    setRejectTarget(role);
    setRejectReason("");
  };

  const submitReject = async () => {
    try {
      await api.post(`/admin/roles/${rejectTarget.id}/reject`, { reason: rejectReason.trim() || null });
      toast.success(`"${rejectTarget.name}" rejected`);
      setRejectTarget(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reject");
    }
  };

  if (!canCreate) {
    return <div className="text-sm text-welljio-muted" data-testid="roles-no-access">You don't have access to this page.</div>;
  }

  return (
    <div data-testid="admin-roles-page" className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-2">Access control</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Roles &amp; permissions</h1>
        </div>
        <button
          data-testid="role-create-button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-welljio-ink text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-slate-800"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Create role
        </button>
      </div>

      {/* Admin's approval queue */}
      {isAdmin && pendingQueue.length > 0 && (
        <div data-testid="pending-roles-queue" className="bg-amber-50 border border-amber-200 rounded-3xl p-5 space-y-3">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
            <Clock className="w-4 h-4" strokeWidth={1.8} />
            {pendingQueue.length} role{pendingQueue.length === 1 ? "" : "s"} waiting for your approval
          </p>
          {pendingQueue.map((role) => (
            <div key={role.id} data-testid={`pending-role-${role.id}`} className="bg-white border border-amber-100 rounded-2xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-welljio-ink text-sm">{role.name}</p>
                  <p className="text-xs text-welljio-subtle mt-0.5">
                    Submitted by {role.submitted_by} &middot; {role.submitted_at && new Date(role.submitted_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  </p>
                  {role.description && <p className="text-xs text-welljio-muted mt-1">{role.description}</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(role.permissions || []).map((p) => (
                  <span key={p} className="text-[10px] bg-welljio-tint text-welljio-muted px-2 py-1 rounded-full">{p}</span>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => approve(role)}
                  data-testid={`approve-role-${role.id}`}
                  className="inline-flex items-center gap-1.5 bg-welljio-ink text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-slate-800"
                >
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
                <button
                  onClick={() => openReject(role)}
                  data-testid={`reject-role-${role.id}`}
                  className="inline-flex items-center gap-1.5 border border-welljio-coralSoft text-welljio-coral rounded-full px-4 py-2 text-xs font-medium hover:bg-welljio-coralSoft"
                >
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-slate-100 rounded-3xl divide-y divide-slate-100">
        {loading ? (
          <div className="px-5 py-6 text-sm text-welljio-muted">Loading roles...</div>
        ) : (
          roles.map((role) => {
            const status = role.status || "active";
            const meta = STATUS_META[status];
            const canEditDelete = isAdmin && !role.is_protected && status === "active";
            return (
              <div key={role.id} data-testid={`role-row-${role.id}`} className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-welljio-tint flex items-center justify-center text-welljio-accent">
                    <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-welljio-ink text-sm">{role.name}</p>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                    </div>
                    <p className="text-xs text-welljio-subtle">
                      {role.staff_count} {role.staff_count === 1 ? "account" : "accounts"}
                      {role.description ? ` · ${role.description}` : ""}
                      {status === "rejected" && role.rejection_reason ? ` · "${role.rejection_reason}"` : ""}
                    </p>
                  </div>
                </div>

                {role.is_protected ? (
                  <span className="text-xs text-welljio-subtle flex items-center gap-1">
                    <Lock className="w-3.5 h-3.5" strokeWidth={1.5} /> Not editable
                  </span>
                ) : canEditDelete ? (
                  <div className="flex items-center gap-2">
                    <button data-testid={`role-edit-${role.id}`} onClick={() => openEdit(role)} className="text-xs rounded-full border border-slate-200 px-3 py-2 hover:bg-welljio-tint inline-flex items-center gap-1.5">
                      <Pencil className="w-3 h-3" strokeWidth={1.5} /> Edit
                    </button>
                    <button data-testid={`role-delete-${role.id}`} onClick={() => remove(role)} className="text-xs rounded-full border border-slate-200 px-3 py-2 hover:bg-red-50 text-red-600">
                      <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                    </button>
                  </div>
                ) : (
                  <span className="text-xs text-welljio-subtle">
                    {status === "pending" ? "Awaiting Admin review" : status === "rejected" ? "Not approved" : ""}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="role-form-dialog" className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? `Editing: ${editing.name}` : "Create role"}</DialogTitle>
          </DialogHeader>

          {isEditingOwnRole && (
            <p className="text-xs text-welljio-coral bg-welljio-coralSoft rounded-2xl px-4 py-3">
              You can't edit the permissions of the role you're currently signed in with.
            </p>
          )}
          {!editing && !isAdmin && (
            <p className="text-xs text-welljio-coral bg-welljio-coralSoft rounded-2xl px-4 py-3">
              New roles need Admin approval before they can be assigned to anyone.
            </p>
          )}

          <form onSubmit={submit} className="space-y-3 mt-2">
            <input
              required
              value={form.name}
              onChange={change("name")}
              disabled={formDisabled}
              placeholder="Role name"
              data-testid="role-form-name"
              className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent disabled:bg-welljio-tint"
            />
            <input
              value={form.description}
              onChange={change("description")}
              disabled={formDisabled}
              placeholder="Description (optional)"
              data-testid="role-form-description"
              className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent disabled:bg-welljio-tint"
            />

            <div className="pt-2">
              <label className="text-xs uppercase tracking-widest text-welljio-subtle">Permissions</label>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5 mt-3">
                {PERMISSION_MODULES.map((mod) => {
                  const reserved = RESERVED_PERMISSIONS.filter((r) => r.module === mod.key);
                  return (
                    <div key={mod.key}>
                      <p className="text-sm font-medium text-welljio-ink mb-2">{mod.label}</p>
                      {mod.items.map((item) => (
                        <label key={item.key} className="flex items-center gap-2 text-sm mb-1.5">
                          <input
                            type="checkbox"
                            checked={form.permissions.includes(item.key)}
                            disabled={formDisabled}
                            onChange={() => togglePermission(item.key)}
                            className="accent-welljio-ink"
                          />
                          {item.label}
                        </label>
                      ))}
                      {reserved.length > 0 && isAdmin && reserved.map((r) => (
                        <label key={r.key} className="flex items-center gap-2 text-sm text-welljio-muted mb-1.5">
                          <input
                            type="checkbox"
                            checked={form.permissions.includes(r.key)}
                            disabled={formDisabled}
                            onChange={() => togglePermission(r.key)}
                            className="accent-welljio-ink"
                          />
                          {r.label}
                          <span className="text-[10px] text-welljio-coral bg-welljio-coralSoft px-1.5 py-0.5 rounded-full">Admin only</span>
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              disabled={saving || formDisabled}
              data-testid="role-form-submit"
              className="w-full bg-welljio-ink text-white rounded-full py-3.5 font-medium hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? "Saving..." : editing ? "Save changes" : !isAdmin ? "Submit for approval" : "Create role"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
        <DialogContent data-testid="reject-role-dialog" className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Reject "{rejectTarget?.name}"?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-welljio-muted">
            This role stays on record as rejected — {rejectTarget?.submitted_by} will be able to see it wasn't approved.
          </p>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason (optional, but helpful for the submitter)"
            rows={3}
            data-testid="reject-reason-input"
            className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"
          />
          <div className="flex items-center gap-3">
            <button
              onClick={submitReject}
              data-testid="reject-role-submit"
              className="bg-red-600 text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-red-700"
            >
              Reject role
            </button>
            <button onClick={() => setRejectTarget(null)} className="text-welljio-muted text-sm px-4">Cancel</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
